import { Component, ElementRef, forwardRef, Input, NgZone, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Store } from '@ngrx/store';
import { getTableAcl } from 'src/app/STORE/actions/tableacl.actions';
import { tableAclRecords } from 'src/app/STORE/selectors/tableacl.selector';
import { ApiConstants } from 'src/app/base/api-constants';
import { appSettingsData } from 'src/app/STORE/selectors/technologystack.selector';
import { getAppSettings } from 'src/app/STORE/actions/technologystack.actions';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from 'src/app/services/util.service';

@Component({
  selector: 'vb-btn-action',
  templateUrl: './btn-action.component.html',
  styleUrls: ['./btn-action.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BtnActionComponent),
      multi: true
    }
  ],
  standalone: false  //NOSONAR
})
export class BtnActionComponent implements OnInit, OnDestroy {
  private renderer = inject(Renderer2);
  private appGlobalService = inject(AppGlobalService);
  private store = inject(Store);
  private elementRef = inject(ElementRef);
  private translate = inject(TranslateService);
  private util = inject(UtilService);
  private zone = inject(NgZone);

  @Input() element: any;
  @Input() currentScreen: any;
  @Input() disableField = false;
  @Input() formData: any;
  @Input() currentTable: any;
  @Input() isFromModernize = false;
  private modelValue: any;
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  disabled: boolean;
  selectedAction: any;
  currentTableId: any;
  settingsData: any = null;
  actions: any[] = [];

  get model() {
    return this.modelValue;
  }

  writeValue(value: any): void {
    this.setSelectedValue(value);
    this.modelValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  set(value: any) {
    this.modelValue = value;
    this.onChange(this.modelValue);
  }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    this.disabled = isDisabled;
  }

  setSelectedValue(value) {
    if (value) {
      this.selectedAction = value;
    }
  }

  handleChange(event: any) {
    this.modelValue = event.value;
    this.onChange(this.modelValue);
    setTimeout(() => {
      if (this.modelValue === 'save_and_close') {
        this.util.showMessage('info', this.translate.instant('SAVE_AND_CLOSE_FOR_POPUP_ONLY'));
      }
    }, 300);
  }

  /**
   * Load actions based on page type
   */
  loadActions() {
    if (this.currentScreen?.pageType === 'list') {
      this.actions = this.getListPageActions();
    } else if (this.currentScreen?.pageType === 'detail') {
      this.actions = this.getDetailPageActions();
    }
  }

  /**
   * Get actions for list page type
   */
  private getListPageActions(): any[] {
    const baseActions = [
      { name: 'New', value: 'new' },
      { name: 'Refresh', value: 'refresh' },
      { name: 'Delete', value: 'delete' },
      { name: 'Import', value: 'import' },
      { name: 'Back', value: 'back' }
    ];

    const softDeleteActions = [
      { name: 'Activate', value: 'activate' },
      { name: 'Deactivate', value: 'deactivate' }
    ];

    const additionalActions = [
      { name: 'Separator', value: 'separator', disabled: true },
      { name: 'Call a Backend webservice', value: 'call_a_backend_webservice' },
      { name: 'Navigate to Page', value: 'navigate_to_page' }
    ];

    return [
      ...baseActions,
      ...(this.settingsData?.deactivateRecords ? softDeleteActions : []),
      ...additionalActions
    ];
  }

  /**
   * Get actions for detail page type
   */
  private getDetailPageActions(): any[] {
    const baseActions = [
      { name: 'Back', value: 'back' },
      { name: 'Save', value: 'save' },
      { name: 'Save and Close', value: 'save_and_close' },
      { name: 'Cancel', value: 'cancel' },
      { name: 'New', value: 'new' },
      { name: 'Delete', value: 'delete' },
      { name: 'Changelog', value: 'changelog' }
    ];

    const softDeleteActions = [
      { name: 'Activate', value: 'activate' },
      { name: 'Deactivate', value: 'deactivate' }
    ];

    const workflowActions = this.getWorkflowActions();

    const additionalActions = [
      { name: 'Separator', value: 'separator', disabled: true },
      { name: 'Call a Backend webservice', value: 'call_a_backend_webservice' },
      { name: 'Navigate to Page', value: 'navigate_to_page' }
    ];

    return [
      ...baseActions,
      ...(this.settingsData?.deactivateRecords ? softDeleteActions : []),
      ...workflowActions,
      ...additionalActions
    ];
  }

  /**
   * Get workflow-related actions for detail page
   */
  private getWorkflowActions(): any[] {
    const actions: any[] = [];
    const isApplicationUser = this.currentScreen?.tableName === 'Application User';
    const isQueryView = this.currentScreen?.pageTableType === 'QUERY_VIEW';

    if (isApplicationUser || isQueryView) {
      return actions;
    }

    actions.push({ name: 'Workflow History', value: 'workflowhistory' });

    if (this.currentTable?.hadRuntimeWorkflow) {
      actions.push({ name: 'Workflow Timeline', value: 'workflowtimeline' });
    }

    if (this.currentTable?.t_runtime_workflow) {
      actions.push({ name: 'Workflow Instance', value: 'workflowinstance' });
    }

    return actions;
  }
  eventTracker = {
    trackedEvents: {} as Record<string, any>,

    start: function (key) {
      const startTime = new Date();
      this.trackedEvents[key] = {
        start: startTime
      };
    },

    stop: function (key) {
      const endTime: any = new Date();
      this.trackedEvents[key].duration = `${this.trackedEvents[key].start} ${endTime}  ========> ${
        (endTime - this.trackedEvents[key].start) / 1000
      }s`;
      this.trackedEvents[key].end = endTime;
    }
  };
  ngOnInit(): void {
    this.store.select(appSettingsData).subscribe((val) => {
      if (val) {
        this.settingsData = val;
      }
    });
    this.store.dispatch(
      getAppSettings({
        apiConstants: ApiConstants.getSettingsById,
        appId: this.appGlobalService.get('appId')
      })
    );
    this.eventTracker.start('appSettingsData');
    const appSettSub = this.store.select(appSettingsData).subscribe((val) => {
      this.eventTracker.stop('appSettingsData');
      // eslint-disable-next-line dot-notation
      console.log(`appSettingsData : ${this.eventTracker.trackedEvents['appSettingsData']?.duration}`);
      if (val) {
        this.settingsData = val;
        this.loadActions();
      }
    });
    this.currentTableId = this.currentScreen.tableId;
    this.store.dispatch(
      getTableAcl({
        apiConstants: ApiConstants.getTableAclBySid,
        tableid: { tableid: this.currentTableId }
      })
    );
    this.loadActions();
    this.store.select(tableAclRecords).subscribe((data) => {
      if (data) {
        console.log(data);
        if (this.currentScreen?.pageType === 'list') {
          const listCustomActions = data.filter(
            (fld) =>
              !this.actions.some((an) => an.name.toLowerCase() === fld.actionName.toLowerCase()) &&
              fld.actionName !== 'CREATE' &&
              fld.actionName !== 'CHANGELOG' &&
              fld.actionName !== 'READ' &&
              fld.actionName !== 'UPDATE' &&
              fld.actionName !== 'CREATE' &&
              fld.actionName !== 'DELETE' &&
              (fld.aclActionType === 'DELETE' || fld.aclActionType === 'READ')
          );
          if (listCustomActions.length > 0) {
            this.actions = [
              ...listCustomActions.map((fld) => {
                return {
                  name: fld.actionName,
                  value: fld.sid
                };
              }),
              ...this.actions
            ];
          }
        } else if (this.currentScreen?.pageType === 'detail') {
          const detailCustomActions = data.filter(
            (fld) =>
              !this.actions.some((an) => an.name.toLowerCase() === fld.actionName.toLowerCase()) &&
              fld.actionName !== 'CREATE' &&
              fld.actionName !== 'READ' &&
              fld.actionName !== 'UPDATE' &&
              fld.actionName !== 'CREATE' &&
              fld.actionName !== 'DELETE' &&
              (fld.aclActionType === 'CREATE' || fld.aclActionType === 'UPDATE' || fld.aclActionType === 'READ')
          );
          if (detailCustomActions.length > 0) {
            this.actions = [
              ...detailCustomActions.map((fld) => {
                return {
                  name: fld.actionName,
                  value: fld.sid
                };
              }),
              ...this.actions
            ];
          }
        }
      }
    });

    if (
      this.formData.wfAction ||
      this.formData.action === 'workflowtimeline' ||
      this.formData.action === 'workflowinstance'
    ) {
      this.disableField = true;
    }
  }

  ngOnDestroy(): void {
    if ($(this.elementRef.nativeElement)) {
      $(this.elementRef.nativeElement).remove();
    }
  }
}
